import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

/**
 * Custom hook that automatically caches form state to localStorage
 * @param {string} formId - Identifier for the form
 * @param {Object} initialState - Initial form state  with field names and default values
 * @param {Object} [options] - Configuration options
 * @returns {Object} State values, setters, and utility functions
 */
export const useFormCache = (formId, initialState, options = {}) => {
  const {
    clearOnSubmit = false,
    excludeFields = [],
    storagePrefix = 'formCache_',
    debounceMs = 300
  } = options;

  const storageKey = `${storagePrefix}${formId}`;
  const initialStateRef = useRef(initialState);
  const validKeys = useMemo(() => Object.keys(initialState), [initialState]);

  useEffect(() => {
    initialStateRef.current = initialState;
  }, [initialState]);

  // Load cached data if available, else returns the initialState
  // Only create cache when user modifies form values
  const getInitialState = useCallback(() => {
    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsedCache = JSON.parse(cached);
        const merged = { ...initialStateRef.current };
        validKeys.forEach(key => {
          if (key in parsedCache) merged[key] = parsedCache[key];
        });
        return merged;
      }
    } catch (error) {
      console.error(`Failed to load cached form data for ${storageKey}:`, error);
    }
    return initialStateRef.current;
  }, [storageKey, validKeys]);

  const getInitialHasChanges = () => {
    const initial = getInitialState();
    return JSON.stringify(initial) !== JSON.stringify(initialStateRef.current);
  };

  const initialHasChanges = getInitialHasChanges();
  const [formState, setFormState] = useState(getInitialState);
  const [hasUserChanges, setHasUserChanges] = useState(initialHasChanges);
  const hasUserChangesRef = useRef(initialHasChanges);
  const debounceTimeoutRef = useRef(null);

  useEffect(() => {
    hasUserChangesRef.current = hasUserChanges;
  }, [hasUserChanges]);

  useEffect(() => {
    const nextState = getInitialState();
    setFormState((prev) => {
      const merged = { ...nextState, ...prev };
      if (JSON.stringify(merged) !== JSON.stringify(prev)) setHasUserChanges(() => true);
      return merged;
    });
  }, [getInitialState]);

  // Save to localStorage
  // Debounces saves to prevent excessive localStorage writes during rapid input reducing UI lag.
  const saveToStorage = useCallback(
    (state) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }

      debounceTimeoutRef.current = setTimeout(() => {
        if (!hasUserChangesRef.current) return;
        try {
          const stateToSave = validKeys.reduce((acc, key) => {
            if (!excludeFields.includes(key)) acc[key] = state[key];
            return acc;
          }, {});

          localStorage.setItem(storageKey, JSON.stringify(stateToSave));
        } catch (error) {
          console.error(`Failed to save form data for ${storageKey}:`, error);
        }
        debounceTimeoutRef.current = null;
      }, debounceMs);
    },
    [storageKey, excludeFields, debounceMs, validKeys]
  );

  useEffect(() => {
    saveToStorage(formState);
  }, [formState, saveToStorage]);

  const values = useMemo(() => {
    const vals = {};
    validKeys.forEach(fieldName => {
      vals[fieldName] = formState[fieldName];
    });
    return vals;
  }, [formState, validKeys]);

  const setters = useMemo(() => {
    const sets = {};
    validKeys.forEach((fieldName) => {
      const setterName = `set${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
      sets[setterName] = (value) => {
        setFormState((prev) => {
          if (prev[fieldName] === value) return prev;
          setHasUserChanges(() => true);
          return { ...prev, [fieldName]: value };
        });
      };
    });
    return sets;
  }, [validKeys]);

  /** 
   * Updates a single field by name (string key), useful for dynamic forms or generic event handlers.
   * Unlike the individual setters which are auto-generated for each field in the initialState, this uses a string key for the field name. 
   * Makes it more flexible for scenarios where field names are: 
   * dynamic, unknown at compile time, or when you want a generic handler that works across any field without specific setter references.
  */
  const updateField = useCallback((fieldName, value) => {
    setFormState(prev => {
      if (prev[fieldName] === value) return prev;
      setHasUserChanges(() => true);
      return { ...prev, [fieldName]: value };
    });
  }, []);

  // This updates multiple fields at once with an object
  // Efficient for bulk updates or resetting multiple fields
  const updateFields = useCallback((updates) => {
    setFormState(prev => {
      const next = { ...prev, ...updates };
      if (JSON.stringify(prev) !== JSON.stringify(next)) setHasUserChanges(() => true);
      return next;
    });
  }, []);

  const clearForm = useCallback(() => {
    setHasUserChanges(false);
    setFormState(initialStateRef.current);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error(`Failed to reset form data for ${storageKey}:`, error);
    }
  }, [storageKey]);

  const handleSubmit = useCallback((submitCallback) => (event) => {
      if (submitCallback) submitCallback(event, formState);
      if (clearOnSubmit) clearForm();
    },
    [clearOnSubmit, clearForm]
  );

  // Cleanup debounce timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }
    };
  }, []);

  return { ...values, ...setters, updateField, updateFields, clearForm, handleSubmit, formState };
};

/** 
 * Higher order component that wraps a form component with localStorage caching
 * @param {React.ComponentType} WrappedComponent - Form component to wrap
 * @param {Object} formId - Identifier for the form
 * @param {Object} initialState - Initial form state  with field names and default values
 * @param {Object} [options] - Configuration options
 * @returns {React.ComponentType} Fully wrapped form component with localStorage caching
 */
export const withFormCache = (WrappedComponent, formId, initialState, options = {}) => {
  return (props) => {
    const cachedFormProps = useFormCache(formId, initialState, options);
    return <WrappedComponent {...props} {...cachedFormProps} />;
  };
};